import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown, FaChevronUp, FaListOl, FaTimes } from "react-icons/fa";
import { projectApi } from "../../Services/api";

const DEFAULT_PROFILE = "driving-car";
const DEFAULT_ROUTING_MODE = "sync";

function getLoggedUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("usuarioLogado"));
    const userId = Number(user?.IDusuario);
    return Number.isInteger(userId) && userId > 0 ? userId : null;
  } catch (error) {
    return null;
  }
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.errors?.default || fallback;
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("pt-BR");
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("pt-BR");
}

function formatDistance(value) {
  const distance = Number(value || 0);
  return distance > 0 ? `${(distance / 1000).toFixed(1)} km` : "0 km";
}

function formatDuration(value) {
  const seconds = Number(value || 0);
  if (seconds <= 0) {
    return "0 min";
  }

  const minutes = Math.round(seconds / 60);
  return minutes >= 60
    ? `${Math.floor(minutes / 60)}h ${minutes % 60}min`
    : `${minutes} min`;
}

function getGroupKey(group) {
  return `${group.IDcliente}:${group.IDendereco}`;
}

function getPlanningStopKey(stop) {
  return `${stop.IDcliente}:${stop.IDendereco}`;
}

function buildClientMapPoints(groups) {
  return groups
    .map((group) => {
      const latitude = Number(group?.coordenadas?.latitude);
      const longitude = Number(group?.coordenadas?.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }

      return {
        id: getGroupKey(group),
        position: [latitude, longitude],
        title: group.nomeCliente || "Cliente",
        subtitle: group.enderecoFormatado || "",
        orders: group.pedidos?.length || 0,
      };
    })
    .filter(Boolean);
}

export default function MicroPlanningPanel({
  routeId,
  routeName,
  selectedStopOrder,
  onClose,
  onMapPointsChange,
  onDirtyChange,
  onStopsPayloadChange,
}) {
  const [stopsPayload, setStopsPayload] = useState(null);
  const [eligiblePayload, setEligiblePayload] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [plannedStops, setPlannedStops] = useState([]);
  const [activePlanningId, setActivePlanningId] = useState(null);
  const [planningPayload, setPlanningPayload] = useState(null);
  const [technicalDraftId, setTechnicalDraftId] = useState(null);
  const [showPlanningOrdersModal, setShowPlanningOrdersModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlanning, setLoadingPlanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshingCoordinateKey, setRefreshingCoordinateKey] = useState(null);
  const [draggedKey, setDraggedKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const loadedContextRef = useRef("");

  const loggedUserId = useMemo(() => getLoggedUserId(), []);
  const stage2Enabled = Boolean(
    stopsPayload?.configuracao?.microRoteamentoEtapa2Habilitada
  );
  const selectedStop = useMemo(
    () =>
      stopsPayload?.paradas?.find(
        (stop) => Number(stop.ordem) === Number(selectedStopOrder)
      ) || null,
    [selectedStopOrder, stopsPayload]
  );
  const eligibleGroups = useMemo(
    () => eligiblePayload?.pedidosAgrupados || [],
    [eligiblePayload]
  );
  const selectedIdSet = useMemo(
    () => new Set(selectedOrderIds),
    [selectedOrderIds]
  );
  const plannedIdSet = useMemo(
    () =>
      new Set(
        plannedStops.flatMap((planningStop) =>
          planningStop.pedidos.map((pedido) => pedido.IDpedido)
        )
      ),
    [plannedStops]
  );
  const isDirty = selectedOrderIds.length > 0 || plannedStops.length > 0;
  const planning = planningPayload?.planejamento || null;
  const orderedPlanningOrders = useMemo(
    () =>
      (planning?.paradas || []).flatMap((planningStop, stopIndex) =>
        (planningStop.pedidos || []).map((pedido) => ({
          ...pedido,
          visitaOrdem:
            planningStop.ordem_planejada ||
            planningStop.ordem_otimizada ||
            stopIndex + 1,
          paradaTipo: planningStop.tipo,
          enderecoFormatado:
            planningStop.endereco_snapshot?.enderecoFormatado ||
            planningStop.endereco_snapshot?.enderecoRoteamento ||
            "",
        }))
      ),
    [planning]
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const contextKey = `${routeId || ""}:${selectedStopOrder || ""}`;
    if (loadedContextRef.current === contextKey) {
      return;
    }

    loadedContextRef.current = contextKey;
    setSelectedOrderIds([]);
    setPlannedStops([]);
    setTechnicalDraftId(null);
    setPlanningPayload(null);
    setActivePlanningId(null);
    setShowPlanningOrdersModal(false);
  }, [routeId, selectedStopOrder]);

  async function loadStopData({ keepMessages = false } = {}) {
    if (!routeId || !selectedStopOrder) {
      setStopsPayload(null);
      setEligiblePayload(null);
      return;
    }

    setLoading(true);
    if (!keepMessages) {
      setErrorMessage("");
    }

    try {
      const [stopsResponse, eligibleResponse] = await Promise.all([
        projectApi.get(`/roteamento/rotas/${routeId}/paradas`),
        projectApi.get(
          `/roteamento/rotas/${routeId}/paradas/${selectedStopOrder}/pedidos`
        ),
      ]);
      const nextStopsPayload = stopsResponse?.data || null;
      setStopsPayload(nextStopsPayload);
      setEligiblePayload(eligibleResponse?.data || null);
      onStopsPayloadChange?.(nextStopsPayload);
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Nao foi possivel carregar o planejamento.")
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStopData();
    // loadStopData intentionally depends only on the selected route/city.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, selectedStopOrder]);

  useEffect(() => {
    onMapPointsChange?.(
      stage2Enabled ? buildClientMapPoints(eligibleGroups) : []
    );
  }, [eligibleGroups, onMapPointsChange, stage2Enabled]);

  useEffect(() => {
    let cancelled = false;

    async function loadPlanning() {
      if (!activePlanningId) {
        setPlanningPayload(null);
        setShowPlanningOrdersModal(false);
        return;
      }

      setLoadingPlanning(true);
      try {
        const response = await projectApi.get(
          `/roteamento/planejamentos/${activePlanningId}`
        );
        if (!cancelled) {
          setPlanningPayload(response?.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            getErrorMessage(
              error,
              "Nao foi possivel carregar o planejamento selecionado."
            )
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingPlanning(false);
        }
      }
    }

    loadPlanning();
    return () => {
      cancelled = true;
    };
  }, [activePlanningId]);

  useEffect(() => {
    if (
      !stage2Enabled ||
      planning?.tipo_planejamento !== "microrroteirizado" ||
      planning?.status_descricao !== "rascunho"
    ) {
      return;
    }

    const deliveryStops = (planning.paradas || []).filter(
      (planningStop) => planningStop.tipo === "entrega"
    );

    if (deliveryStops.length === 0) {
      return;
    }

    setTechnicalDraftId(planning.IDplanejamento);
    setPlannedStops(
      deliveryStops.map((planningStop) => ({
        key: getPlanningStopKey(planningStop),
        IDcliente: planningStop.IDcliente,
        IDendereco: planningStop.IDendereco,
        nomeCliente:
          planningStop.pedidos?.[0]?.nomeCliente ||
          planningStop.pedidos?.[0]?.razaoSocial ||
          `Cliente ${planningStop.IDcliente}`,
        enderecoFormatado:
          planningStop.endereco_snapshot?.enderecoFormatado ||
          planningStop.endereco_snapshot?.enderecoRoteamento ||
          "",
        coordenadas: {
          latitude: planningStop.latitude,
          longitude: planningStop.longitude,
        },
        pedidos: planningStop.pedidos || [],
      }))
    );
  }, [planning, stage2Enabled]);

  function toggleOrder(IDpedido) {
    if (plannedIdSet.has(IDpedido)) {
      return;
    }

    setSelectedOrderIds((current) =>
      current.includes(IDpedido)
        ? current.filter((item) => item !== IDpedido)
        : [...current, IDpedido]
    );
  }

  function toggleGroup(group) {
    const availableIds = group.pedidos
      .map((pedido) => pedido.IDpedido)
      .filter((IDpedido) => !plannedIdSet.has(IDpedido));
    const shouldSelect = availableIds.some(
      (IDpedido) => !selectedIdSet.has(IDpedido)
    );

    setSelectedOrderIds((current) => {
      const next = new Set(current);
      availableIds.forEach((IDpedido) =>
        shouldSelect ? next.add(IDpedido) : next.delete(IDpedido)
      );
      return Array.from(next);
    });
  }

  function handlePlanLocally() {
    if (selectedOrderIds.length === 0) {
      setErrorMessage("Selecione ao menos um pedido antes de planejar.");
      return;
    }

    const selected = new Set(selectedOrderIds);
    const additions = eligibleGroups
      .map((group) => ({
        key: getGroupKey(group),
        IDcliente: group.IDcliente,
        IDendereco: group.IDendereco,
        nomeCliente: group.nomeCliente,
        enderecoFormatado: group.enderecoFormatado,
        coordenadas: group.coordenadas,
        pedidos: group.pedidos.filter((pedido) =>
          selected.has(pedido.IDpedido)
        ),
      }))
      .filter((group) => group.pedidos.length > 0);

    setPlannedStops((current) => {
      const next = current.map((item) => ({ ...item, pedidos: [...item.pedidos] }));

      additions.forEach((addition) => {
        const existing = next.find((item) => item.key === addition.key);

        if (existing) {
          const existingIds = new Set(
            existing.pedidos.map((pedido) => pedido.IDpedido)
          );
          existing.pedidos.push(
            ...addition.pedidos.filter(
              (pedido) => !existingIds.has(pedido.IDpedido)
            )
          );
        } else {
          next.push(addition);
        }
      });

      return next;
    });
    setSelectedOrderIds([]);
    setPlanningPayload(null);
    setActivePlanningId(null);
    setSuccessMessage("Pedidos incluidos na lista local de planejamento.");
    setErrorMessage("");
  }

  function moveStop(index, direction) {
    const target = index + direction;
    if (target < 0 || target >= plannedStops.length) {
      return;
    }

    setPlannedStops((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function removeStop(index) {
    setPlannedStops((current) =>
      current.filter((_, currentIndex) => currentIndex !== index)
    );
  }

  function handleDrop(targetKey) {
    if (!draggedKey || draggedKey === targetKey) {
      setDraggedKey(null);
      return;
    }

    setPlannedStops((current) => {
      const sourceIndex = current.findIndex((item) => item.key === draggedKey);
      const targetIndex = current.findIndex((item) => item.key === targetKey);
      if (sourceIndex < 0 || targetIndex < 0) {
        return current;
      }

      const next = [...current];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDraggedKey(null);
  }

  function buildExplicitStops(orderField) {
    return plannedStops.map((planningStop, index) => ({
      [orderField]: index + 1,
      IDcliente: planningStop.IDcliente,
      IDendereco: planningStop.IDendereco,
      IDsPedido: planningStop.pedidos.map((pedido) => pedido.IDpedido),
    }));
  }

  async function handleConfirm() {
    if (!loggedUserId || plannedStops.length === 0) {
      setErrorMessage("Monte o planejamento e confirme o usuario logado.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      let response;

      if (technicalDraftId) {
        const technicalStops = planning?.paradas?.filter(
          (planningStop) => planningStop.tipo === "entrega"
        ) || [];
        const technicalStopByKey = new Map(
          technicalStops.map((planningStop) => [
            getPlanningStopKey(planningStop),
            planningStop,
          ])
        );

        response = await projectApi.post(
          `/roteamento/planejamentos/${technicalDraftId}/confirmar`,
          {
            IDusuarioConfirmacao: loggedUserId,
            paradas: plannedStops.map((planningStop, index) => ({
              ordemPlanejada: index + 1,
              IDplanejamentoParada:
                technicalStopByKey.get(planningStop.key)
                  ?.IDplanejamentoParada,
            })),
          }
        );
      } else {
        response = await projectApi.post(
          "/roteamento/planejamentos/manuais/confirmar",
          {
            IDrotaMacro: routeId,
            parada_macro_ordem: selectedStopOrder,
            IDusuarioConfirmacao: loggedUserId,
            paradas: buildExplicitStops("ordemPlanejada"),
          }
        );
      }

      const confirmedId = response?.data?.planejamento?.IDplanejamento || null;
      setPlanningPayload(response?.data || null);
      setActivePlanningId(confirmedId);
      setTechnicalDraftId(null);
      setSelectedOrderIds([]);
      setPlannedStops([]);
      await loadStopData({ keepMessages: true });
      setSuccessMessage("Planejamento confirmado com sucesso.");
    } catch (error) {
      await loadStopData({ keepMessages: true });
      setErrorMessage(
        getErrorMessage(
          error,
          "Nao foi possivel confirmar. Revise os pedidos ainda disponiveis."
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMicroRoute() {
    if (!stage2Enabled || !loggedUserId || plannedStops.length === 0) {
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await projectApi.post("/roteamento/planejamentos", {
        IDplanejamento: technicalDraftId,
        IDrotaMacro: routeId,
        parada_macro_ordem: selectedStopOrder,
        IDusuarioCriacao: loggedUserId,
        profile: DEFAULT_PROFILE,
        routingMode: DEFAULT_ROUTING_MODE,
        paradas: buildExplicitStops("ordemAtual"),
      });
      const payload = response?.data || null;
      const draftId = payload?.planejamento?.IDplanejamento || null;
      const suggestedKeys = (payload?.ordemSugerida || []).map(
        (item) => `${item.IDcliente}:${item.IDendereco}`
      );

      setPlannedStops((current) => {
        const byKey = new Map(current.map((item) => [item.key, item]));
        return [
          ...suggestedKeys.map((key) => byKey.get(key)).filter(Boolean),
          ...current.filter((item) => !suggestedKeys.includes(item.key)),
        ];
      });
      setTechnicalDraftId(draftId);
      setActivePlanningId(draftId);
      setPlanningPayload(payload);
      await loadStopData({ keepMessages: true });
      setSuccessMessage(
        payload?.paradasSemCoordenada?.length
          ? "Sugestao aplicada. Enderecos sem coordenada foram mantidos ao final."
          : "Sugestao de micro-roteamento aplicada."
      );
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Nao foi possivel executar o micro-roteamento.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRefreshCoordinates(group) {
    const key = getGroupKey(group);
    setRefreshingCoordinateKey(key);
    setErrorMessage("");

    try {
      await projectApi.post(
        `/roteamento/rotas/${routeId}/paradas/${selectedStopOrder}/pedidos/${group.IDcliente}/${group.IDendereco}/geocodificar`
      );
      await loadStopData({ keepMessages: true });
      setSuccessMessage("Coordenadas atualizadas.");
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Nao foi possivel atualizar as coordenadas.")
      );
    } finally {
      setRefreshingCoordinateKey(null);
    }
  }

  async function handleCancel() {
    if (!planning?.IDplanejamento) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await projectApi.post(
        `/roteamento/planejamentos/${planning.IDplanejamento}/cancelar`
      );
      setPlanningPayload(response?.data || null);
      await loadStopData({ keepMessages: true });
      setSuccessMessage("Planejamento cancelado e pedidos liberados.");
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Nao foi possivel cancelar o planejamento.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSynchronize() {
    if (!planning?.IDplanejamento || !stage2Enabled) {
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      const response = await projectApi.post(
        `/roteamento/planejamentos/${planning.IDplanejamento}/sincronizar`
      );
      setPlanningPayload(response?.data || null);
      await loadStopData({ keepMessages: true });
      setSuccessMessage("Sincronizacao executada.");
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Nao foi possivel sincronizar o planejamento.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    if (
      isDirty &&
      !window.confirm(
        "Existem alteracoes locais nao confirmadas. Deseja fechar e descarta-las?"
      )
    ) {
      return;
    }
    onClose?.(true);
  }

  function handleSelectPlanning(IDplanejamento) {
    if (IDplanejamento === activePlanningId) {
      setActivePlanningId(null);
      setPlanningPayload(null);
      setShowPlanningOrdersModal(false);
      return;
    }

    if (
      isDirty &&
      !window.confirm(
        "Existem alteracoes locais nao confirmadas. Deseja abrir outro planejamento e descarta-las?"
      )
    ) {
      return;
    }

    setSelectedOrderIds([]);
    setPlannedStops([]);
    setTechnicalDraftId(null);
    setShowPlanningOrdersModal(false);
    setActivePlanningId(IDplanejamento);
  }

  function closePlanningOrdersModal() {
    setShowPlanningOrdersModal(false);
  }

  const availableGroups = eligibleGroups.filter((group) =>
    group.pedidos.some((pedido) => !plannedIdSet.has(pedido.IDpedido))
  );

  return (
    <div className="route-micro-panel">
      <div className="route-micro-header">
        <div>
          <h3>Planejamento de entregas</h3>
          {selectedStop ? (
            <div className="route-micro-header-summary">
              <strong>
                Cidade {selectedStop.ordem}: {selectedStop.nome}
              </strong>
            </div>
          ) : null}
          <p>{routeName ? `Rota macro: ${routeName}` : "Rota nao selecionada"}</p>
          {selectedStop ? (
            <span className="route-micro-header-confirmed">
              {selectedStop.totalPlanejamentosConfirmados || 0} confirmado(s)
            </span>
          ) : null}
        </div>
        <div className="route-micro-header-actions">
          <button
            type="button"
            className="route-micro-close"
            onClick={handleClose}
            aria-label="Fechar planejamento"
          >
            x
          </button>
        </div>
      </div>


      <div className="route-micro-card">
        <div className="route-micro-card-header">
          <strong>Pedidos disponiveis</strong>
          <span>{selectedOrderIds.length} selecionado(s)</span>
        </div>

        {loading ? <p className="route-empty-state">Carregando...</p> : null}
        {!loading && availableGroups.length === 0 ? (
          <p className="route-empty-state">Nenhum pedido disponivel.</p>
        ) : null}

        <div className="route-micro-order-list">
          {availableGroups.map((group) => {
            const availableOrders = group.pedidos.filter(
              (pedido) => !plannedIdSet.has(pedido.IDpedido)
            );
            const allSelected = availableOrders.every((pedido) =>
              selectedIdSet.has(pedido.IDpedido)
            );

            return (
              <div key={getGroupKey(group)} className="route-micro-order-card">
                <div className="route-micro-order-card-header">
                  <div>
                    <strong>{group.nomeCliente}</strong>
                    <span>{group.enderecoFormatado}</span>
                  </div>
                  <div className="route-micro-order-card-actions">
                    {stage2Enabled ? (
                      <button
                        type="button"
                        className="route-micro-inline-button"
                        disabled={refreshingCoordinateKey === getGroupKey(group)}
                        onClick={() => handleRefreshCoordinates(group)}
                      >
                        {refreshingCoordinateKey === getGroupKey(group)
                          ? "Atualizando..."
                          : "Atualizar coord."}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="route-micro-inline-button"
                      onClick={() => toggleGroup(group)}
                    >
                      {allSelected ? "Remover selecao" : "Selecionar endereco"}
                    </button>
                  </div>
                </div>

                {group.possuiPedidosConfirmadosNoMesmoEndereco ? (
                  <div className="route-micro-conflict">
                    Este endereco possui pedidos em outro planejamento confirmado.
                  </div>
                ) : null}

                <div className="route-micro-order-items">
                  {availableOrders.map((pedido) => (
                    <label
                      key={pedido.IDpedido}
                      className="route-micro-order-checkbox"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIdSet.has(pedido.IDpedido)}
                        onChange={() => toggleOrder(pedido.IDpedido)}
                      />
                      <span>
                        Pedido {pedido.IDpedido} | {pedido.natureza || "Sem natureza"} |
                        {" "}{formatDate(pedido.dataPedido)} |{" "}
                        {Number(pedido.volumes || 0)} volume(s)
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="route-actions">
          <button
            type="button"
            className="primary"
            onClick={handlePlanLocally}
            disabled={selectedOrderIds.length === 0}
          >
            Planejar
          </button>
        </div>
      </div>

      <div className="route-micro-card">
        <div className="route-micro-card-header">
          <strong>Planejamento</strong>
          <span>{plannedStops.length} parada(s)</span>
        </div>

        {plannedStops.length === 0 ? (
          <p className="route-empty-state">
            Selecione pedidos e use Planejar para montar a ordem local.
          </p>
        ) : null}

        <div className="route-micro-planned-list">
          {plannedStops.map((planningStop, index) => (
            <div
              key={planningStop.key}
              className="route-micro-planned-stop"
              draggable
              onDragStart={() => setDraggedKey(planningStop.key)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDrop(planningStop.key)}
            >
              <div className="route-micro-order-controls">
                <button
                  type="button"
                  className="route-micro-order-button"
                  onClick={() => moveStop(index, -1)}
                  disabled={index === 0}
                  aria-label={`Subir parada ${index + 1}`}
                  title="Subir"
                >
                  <FaChevronUp aria-hidden="true" />
                </button>
                <span className="route-micro-position">{index + 1}</span>
                <button
                  type="button"
                  className="route-micro-order-button"
                  onClick={() => moveStop(index, 1)}
                  disabled={index === plannedStops.length - 1}
                  aria-label={`Descer parada ${index + 1}`}
                  title="Descer"
                >
                  <FaChevronDown aria-hidden="true" />
                </button>
              </div>
              <div className="route-micro-planned-content">
                <strong>{planningStop.nomeCliente}</strong>
                <span>{planningStop.enderecoFormatado}</span>
                <small>
                  {planningStop.pedidos.length} pedido(s) |{" "}
                  {planningStop.pedidos.reduce(
                    (total, pedido) => total + Number(pedido.volumes || 0),
                    0
                  )}{" "}
                  volume(s)
                </small>
              </div>
              <button
                type="button"
                className="route-micro-remove-button"
                onClick={() => removeStop(index)}
                aria-label={`Remover parada ${index + 1}`}
                title="Remover"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>

        <div className="route-actions">
          {stage2Enabled ? (
            <button
              type="button"
              className="secondary"
              onClick={handleMicroRoute}
              disabled={submitting || plannedStops.length === 0}
            >
              {technicalDraftId ? "Microrotear novamente" : "Microrotear"}
            </button>
          ) : null}
          <button
            type="button"
            className="primary"
            onClick={handleConfirm}
            disabled={submitting || plannedStops.length === 0}
          >
            {submitting ? "Processando..." : "Confirmar"}
          </button>
        </div>
      </div>

      <div className="route-micro-card">
        <div className="route-micro-card-header">
          <strong>Historico da cidade</strong>
          <span>{selectedStop?.planejamentos?.length || 0} registro(s)</span>
        </div>

        <div className="route-micro-planning-list">
          {(selectedStop?.planejamentos || []).map((item) => (
            <button
              key={item.IDplanejamento}
              type="button"
              className={`route-micro-planning-button${
                activePlanningId === item.IDplanejamento ? " active" : ""
              }`}
              onClick={() => handleSelectPlanning(item.IDplanejamento)}
            >
              <strong>
                {item.tipo_planejamento_descricao} #{item.IDplanejamento}
              </strong>
              <span>
                {item.status_descricao} | {item.totalPedidos || 0} pedido(s)
              </span>
            </button>
          ))}
        </div>

        {loadingPlanning ? (
          <p className="route-empty-state">Carregando detalhes...</p>
        ) : null}

        {planning ? (
          <div className="route-micro-history-detail">
            <div className="route-micro-history-detail-header">
              <strong>Detalhes do planejamento</strong>
              <button
                type="button"
                onClick={() => handleSelectPlanning(activePlanningId)}
                aria-label="Recolher detalhes do planejamento"
                title="Recolher detalhes"
              >
                <FaChevronUp aria-hidden="true" />
              </button>
            </div>
            <div className="route-micro-context-grid">
              <div>
                <span>Status</span>
                <strong>{planning.status_descricao}</strong>
              </div>
              <div>
                <span>Tipo</span>
                <strong>{planning.tipo_planejamento_descricao}</strong>
              </div>
              <div>
                <span>Confirmado em</span>
                <strong>{formatDateTime(planning.dataConfirmacao)}</strong>
              </div>
              {planning.tipo_planejamento !== "manual" ? (
                <div>
                  <span>Metricas</span>
                  <strong>
                    {formatDistance(planning.total_distancia)} |{" "}
                    {formatDuration(planning.total_duracao)}
                  </strong>
                </div>
              ) : null}
            </div>
            <div className="route-actions">
              {stage2Enabled &&
              planning.tipo_planejamento === "microrroteirizado" ? (
                <button
                  type="button"
                  className="secondary"
                  onClick={handleSynchronize}
                  disabled={
                    submitting ||
                    planning.status_processamento_descricao !== "processando"
                  }
                >
                  Sincronizar
                </button>
              ) : null}
              <button
                type="button"
                className="secondary"
                onClick={() => setShowPlanningOrdersModal(true)}
                disabled={orderedPlanningOrders.length === 0}
              >
                <FaListOl aria-hidden="true" /> Visualizar pedidos
              </button>
              <button
                type="button"
                className="warning"
                onClick={handleCancel}
                disabled={submitting || planning.status_descricao === "cancelado"}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {errorMessage ? <p className="route-error">{errorMessage}</p> : null}
      {successMessage ? <p className="route-success">{successMessage}</p> : null}

      {showPlanningOrdersModal
        ? createPortal(
            <div
              className="route-modal-overlay"
              onClick={closePlanningOrdersModal}
            >
              <div
                className="route-modal route-planning-orders-modal"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="route-modal-header">
                  <h3>Pedidos do planejamento #{planning?.IDplanejamento}</h3>
                  <button
                    type="button"
                    className="route-modal-close"
                    onClick={closePlanningOrdersModal}
                    aria-label="Fechar pedidos do planejamento"
                  >
                    x
                  </button>
                </div>

                <div className="route-modal-list route-planning-orders-list">
                  {orderedPlanningOrders.length === 0 ? (
                    <p>Nenhum pedido vinculado a este planejamento.</p>
                  ) : null}

                  {orderedPlanningOrders.map((pedido, index) => (
                    <div
                      key={`${pedido.IDplanejamentoPedido}-${pedido.IDpedido}`}
                      className="route-planning-order-item"
                    >
                      <div className="route-planning-order-position">
                        <span>{index + 1}</span>
                        <small>Visita {pedido.visitaOrdem}</small>
                      </div>

                      <div className="route-planning-order-content">
                        <div className="route-planning-order-title">
                          <strong>Pedido {pedido.IDpedido}</strong>
                        </div>
                        <span>
                          {pedido.nomeCliente ||
                            pedido.razaoSocial ||
                            "Cliente sem nome"}
                        </span>
                        {pedido.enderecoFormatado ? (
                          <small>{pedido.enderecoFormatado}</small>
                        ) : null}
                        <div className="route-planning-order-meta">
                          <span>{pedido.natureza || "Sem natureza"}</span>
                          <span>{formatDate(pedido.dataPedido)}</span>
                          <span>{Number(pedido.volumes || 0)} volume(s)</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
